import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

function FeaturesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#pricing') {
      const el = document.getElementById('pricing');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [location]);

  const trackRegisterClick = (label) => {
    if (window.gtag) {
      window.gtag('event', 'click_register', {
        event_category: 'acquisition',
        event_label: label,
      });
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section - Admin focused */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center" style={{ minHeight: '500px' }}>
            <div className="col-lg-7 col-md-6">
              <div className="hero-content">
                <h1 className="hero-title">
                  The Modern Church<br />Directory Platform
                </h1>
                <p className="hero-subtitle">
                  Manage your congregation with a living directory that members update themselves.
                  Track attendance, automate communications, and keep your church connected.
                </p>
                <div className="hero-features">
                  <div className="hero-feature">
                    <i className="fas fa-users"></i>
                    <span>Unlimited members — always free for them</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-magic"></i>
                    <span>Auto-extract faces from family photos</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-chart-line"></i>
                    <span>Attendance tracking & trend reports</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-envelope"></i>
                    <span>Automated birthday & anniversary messages</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-gift"></i>
                    <span>Limited time: 3 months free — no credit card</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-5">
              <div className="card shadow-lg login-card">
                <div className="card-body text-center">
                  <h5 className="card-title mb-3">Start Your Free Trial</h5>
                  <p className="text-muted mb-3" style={{ fontSize: '0.95rem' }}>
                    Set up your church directory in minutes. Your members join for free.
                  </p>
                  <Link to="/register-organization" className="btn btn-primary btn-lg w-100 mb-3" onClick={() => trackRegisterClick('hero_card')}>
                    Register Your Church
                  </Link>
                  <p className="text-muted small mb-2">No credit card required</p>
                  <hr />
                  <p className="signup-text mb-2">Already have an account?</p>
                  <Link to="/login" className="btn btn-outline-secondary w-100">
                    Sign In
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started Steps */}
      <section className="email-section">
        <div className="container">
          <h2 className="text-center mb-2">Up and Running in Minutes</h2>
          <p className="text-center mb-4">No technical setup. No complicated imports. Just add your families and go.</p>
          <div className="row steps-row">
            <div className="col-md-4 text-center mb-3 mb-md-0 step-col">
              <div className="step-icon">
                <i className="fas fa-church"></i>
              </div>
              <h5>1. Register Your Church</h5>
              <p className="small mb-0">Create your organization in under a minute.</p>
            </div>
            <div className="col-md-4 text-center mb-3 mb-md-0 step-col">
              <div className="step-icon highlighted">
                <i className="fas fa-users"></i>
              </div>
              <h5>2. Add Your Families</h5>
              <p className="small mb-0">Upload family photos and contact information.</p>
            </div>
            <div className="col-md-4 text-center step-col step-col-last">
              <div className="step-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h5>3. Members Join Free</h5>
              <p className="small mb-0">Members sign up with their email and are automatically linked — at no cost to them.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Beautiful on Every Device - Mobile Apps */}
      <section className="mobile-section">
        <div className="container">
          <div className="mobile-content">
            <h2>Beautiful on Every Device</h2>
            <p className="mobile-subtitle">
              Your members get free native iPhone and Android apps with push notifications,
              offline access, and tap-to-call — included with every church account.
            </p>
            <div className="mobile-features-grid">
              <div className="mobile-feature-item">
                <i className="fas fa-bell"></i>
                <span>Push Notifications</span>
              </div>
              <div className="mobile-feature-item">
                <i className="fas fa-phone-alt"></i>
                <span>Tap-to-Call</span>
              </div>
              <div className="mobile-feature-item">
                <i className="fas fa-signal"></i>
                <span>Offline Access</span>
              </div>
              <div className="mobile-feature-item">
                <i className="fas fa-camera"></i>
                <span>Photo Upload</span>
              </div>
            </div>
            <div className="mobile-screenshots">
              <div className="mobile-phone-frame">
                <img src="/assets/images/Mobile/Home.png" alt="Church Aware mobile app home screen showing celebrations, events, and groups" className="mobile-screenshot-img" />
              </div>
              <div className="mobile-phone-frame">
                <img src="/assets/images/Mobile/Contacts.png" alt="Church Aware mobile app showing Contacts page with tap-to-call" className="mobile-screenshot-img" />
              </div>
              <div className="mobile-phone-frame">
                <img src="/assets/images/Mobile/Family.png" alt="Church Aware mobile app showing Families page with family photos" className="mobile-screenshot-img" />
              </div>
            </div>
            <div className="mobile-app-links">
              <a href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1" target="_blank" rel="noopener noreferrer" className="app-store-btn">
                <i className="fab fa-apple"></i> Download on the App Store
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware" target="_blank" rel="noopener noreferrer" className="app-store-btn">
                <i className="fab fa-google-play"></i> Get it on Google Play
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="pain-points-section">
        <div className="container">
          <h2 className="text-center">Sound Familiar?</h2>
          <p className="text-center pain-points-subtitle">If you're dealing with any of these, you're not alone.</p>
          <div className="pain-points-grid">
            <div className="pain-point-card">
              <i className="fas fa-print"></i>
              <p>Your printed directory is outdated before the ink dries</p>
            </div>
            <div className="pain-point-card">
              <i className="fas fa-file-excel"></i>
              <p>Member info lives in spreadsheets only one person can update</p>
            </div>
            <div className="pain-point-card">
              <i className="fas fa-question-circle"></i>
              <p>New visitors show up and nobody knows their name next week</p>
            </div>
            <div className="pain-point-card">
              <i className="fas fa-birthday-cake"></i>
              <p>Birthdays and anniversaries slip by without anyone noticing</p>
            </div>
            <div className="pain-point-card">
              <i className="fas fa-phone-slash"></i>
              <p>Members can't easily find each other's phone numbers</p>
            </div>
            <div className="pain-point-card">
              <i className="fas fa-user-edit"></i>
              <p>You're the only one who can update the directory — and it's a chore</p>
            </div>
          </div>
          <p className="text-center pain-points-solution">
            There's a better way to manage your church directory.
          </p>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial-section">
        <div className="container">
          <div className="testimonial-card">
            <i className="fas fa-quote-left testimonial-quote-icon"></i>
            <blockquote className="testimonial-text">
              Our members constantly tell us how much they love using Church Aware. It keeps our church connected and saved me so much time!
            </blockquote>
            <div className="testimonial-author">
              <i className="fas fa-church testimonial-church-icon"></i>
              <span>Little Creek Fellowship</span>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Showcase */}
      <section className="showcase-section">
        <div className="container">
          <h2 className="text-center mb-2" style={{ color: '#1e3a5f', fontWeight: 700, fontSize: '2rem' }}>See It In Action</h2>
          <p className="text-center mb-4" style={{ color: '#64748b', fontSize: '1.05rem' }}>Everything you need to manage your church directory — all in one place.</p>

          {/* Row 1: Dashboard - Image Left */}
          <div className="showcase-row">
            <div className="showcase-image-wrap">
              <img src="/assets/images/Web/dashboard.png" alt="Church Aware Dashboard showing family stats, upcoming events, and birthday reminders" className="showcase-image" />
            </div>
            <div className="showcase-text">
              <h3>Your Church at a Glance</h3>
              <p>See everything that matters in one place — family count, member stats, upcoming events, and this month's birthdays and anniversaries.</p>
              <ul className="showcase-features">
                <li><i className="fas fa-check-circle"></i> Real-time membership stats</li>
                <li><i className="fas fa-check-circle"></i> Upcoming events at a glance</li>
                <li><i className="fas fa-check-circle"></i> Birthday & anniversary tracking</li>
              </ul>
            </div>
          </div>

          {/* Row 2: Families - Image Right */}
          <div className="showcase-row showcase-row-reverse">
            <div className="showcase-image-wrap">
              <img src="/assets/images/Web/families.png" alt="Church directory showing family photos, contact details, and member avatars" className="showcase-image" />
            </div>
            <div className="showcase-text">
              <h3>Beautiful Photo Directory</h3>
              <p>Manage your church family with rich photo cards. Faces are automatically extracted from family photos and can be assigned to each member. Download a professional PDF directory anytime.</p>
              <ul className="showcase-features">
                <li><i className="fas fa-check-circle"></i> Auto-extract faces from family photos</li>
                <li><i className="fas fa-check-circle"></i> Assign individual portraits to each member</li>
                <li><i className="fas fa-check-circle"></i> Instant search & one-click PDF download</li>
              </ul>
            </div>
          </div>

          {/* Row 3: Three-column grid - Groups, Events, Communications */}
          <div className="showcase-grid-row">
            <div className="showcase-grid-card">
              <img src="/assets/images/Web/groups.png" alt="Groups page showing Deacons and Elders with member photos" className="showcase-grid-image" />
              <h4>Organize Your People</h4>
              <p>Create groups like Deacons, Elders, or committees. See members at a glance with their photos.</p>
            </div>
            <div className="showcase-grid-card">
              <img src="/assets/images/Web/events.png" alt="Events page showing upcoming church events with dates and descriptions" className="showcase-grid-image" />
              <h4>Events & Attendance</h4>
              <p>Create church events, send notifications, and track attendance trends over time.</p>
            </div>
            <div className="showcase-grid-card">
              <img src="/assets/images/Web/communications.png" alt="Communications page showing automated birthday, anniversary, and welcome messages" className="showcase-grid-image" />
              <h4>Automated Communications</h4>
              <p>Set up automated birthday greetings, anniversary messages, and welcome emails.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <h2 className="text-center">Common Questions</h2>
          <p className="text-center faq-subtitle">Everything you need to know before getting started.</p>
          <div className="faq-grid">
            <div className="faq-item">
              <h4><i className="fas fa-clock"></i> How long does setup take?</h4>
              <p>Most churches are up and running in under 15 minutes. Register, add your families, and you're done. Members can start joining immediately.</p>
            </div>
            <div className="faq-item">
              <h4><i className="fas fa-dollar-sign"></i> What happens after the free trial?</h4>
              <p>After your 3-month free trial, it's just $10/month for the entire church — unlimited members. You'll get a reminder before any charges. Cancel anytime.</p>
            </div>
            <div className="faq-item">
              <h4><i className="fas fa-lock"></i> Is our data secure?</h4>
              <p>Yes. Church Aware uses encrypted data, secure authentication, and strict per-organization isolation. Your data is never shared or sold.</p>
            </div>
            <div className="faq-item">
              <h4><i className="fas fa-user-friends"></i> What if our members aren't tech-savvy?</h4>
              <p>Members don't need to do anything. Admins add families and the directory works immediately. Members can optionally create a free account to update their own info.</p>
            </div>
            <div className="faq-item">
              <h4><i className="fas fa-exchange-alt"></i> Can we import our existing directory?</h4>
              <p>Yes! You can import your directory from a CSV file or paste a list of names and emails. We'll automatically group families and get you set up in minutes. You can also add families one by one through a simple form.</p>
            </div>
            <div className="faq-item">
              <h4><i className="fas fa-credit-card"></i> Do members have to pay anything?</h4>
              <p>Never. Member accounts are always 100% free. Only the church pays one flat fee — no per-member charges, no hidden costs, no surprises.</p>
            </div>
            <div className="faq-item">
              <h4><i className="fas fa-download"></i> Can we export our directory data?</h4>
              <p>Yes! Admins can export the full directory as a professional PDF or as a CSV file for use in Excel, mail merges, or backups. Both options are available with one click.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <h2 className="text-center" style={{ color: '#1e3a5f', fontWeight: 600, marginBottom: '0.5rem' }}>Simple, Transparent Pricing</h2>
          <p className="text-center" style={{ color: '#64748b', marginBottom: '2rem' }}>One plan for your church. Every member joins free.</p>
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row g-4">
                {/* Churches - Primary */}
                <div className="col-md-6">
                  <div className="pricing-card pricing-card-featured text-center h-100">
                    <p className="card-context">Everything your church needs</p>
                    <h3>Church Account</h3>
                    <div className="price">
                      <span className="amount">$10</span>
                      <span className="period">/month for the entire church</span>
                    </div>
                    <p className="trial-badge">Limited Time: 3-Month Free Trial</p>
                    <ul className="pricing-list">
                      <li><i className="fas fa-check"></i> Unlimited members & admins</li>
                      <li><i className="fas fa-check"></i> Events, groups & attendance tracking</li>
                      <li><i className="fas fa-check"></i> Auto face extraction from photos</li>
                      <li><i className="fas fa-check"></i> Automated communications</li>
                      <li><i className="fas fa-check"></i> PDF & CSV directory exports</li>
                      <li><i className="fas fa-check"></i> iPhone & Android apps for all members</li>
                    </ul>
                    <Link to="/register-organization" className="btn btn-primary btn-lg w-100" onClick={() => trackRegisterClick('pricing_card')}>
                      Register Your Church
                    </Link>
                    <p className="mt-3 text-muted small">No credit card required</p>
                  </div>
                </div>
                {/* Members - Free */}
                <div className="col-md-6">
                  <div className="pricing-card text-center h-100">
                    <p className="card-context">For every member of your church</p>
                    <h3>Member Accounts</h3>
                    <div className="price">
                      <span className="amount">Free</span>
                    </div>
                    <p className="text-muted mb-3">Always free — members never pay</p>
                    <ul className="pricing-list">
                      <li><i className="fas fa-check"></i> Photo directory & tap-to-call contacts</li>
                      <li><i className="fas fa-check"></i> Birthday & anniversary notifications</li>
                      <li><i className="fas fa-check"></i> Update their own family info & photos</li>
                      <li><i className="fas fa-check"></i> iPhone & Android mobile apps</li>
                      <li><i className="fas fa-check"></i> Automatically linked by email</li>
                    </ul>
                    <div className="pricing-included-badge">
                      <i className="fas fa-check-circle"></i> Included With Your Church
                    </div>
                    <p className="mt-3 text-muted small">No cost to your members — ever</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <div className="container text-center">
          <h2>Ready to Get Your Church Connected?</h2>
          <p>Start your free trial today. No credit card required.</p>
          <p style={{ opacity: 0.85, fontSize: '0.95rem' }}>Questions? Email us at <a href="mailto:contact@churchaware.com" style={{ color: 'white', textDecoration: 'underline' }}>contact@churchaware.com</a></p>
          <div className="cta-buttons">
            <Link to="/register-organization" className="btn btn-light btn-lg" onClick={() => trackRegisterClick('bottom_cta')}>
              Register Your Church
            </Link>
          </div>
          <div className="app-links mt-4">
            <a href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple"></i> App Store
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-google-play"></i> Google Play
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FeaturesPage;
